(() => {
  const productRoots = document.querySelectorAll('.product-template');

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const normalizeLinkedColorText = (value) =>
    String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();

  const getLinkedColorHandle = (url) => {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const productMatch = pathname.match(/\/products\/([^/?#]+)/i);
      return productMatch ? decodeURIComponent(productMatch[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const getCleanLinkedColorUrl = (url, handle) => {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const variantId = parsedUrl.searchParams.get('variant');
      const cleanUrl = new URL(parsedUrl.pathname, window.location.origin);

      if (variantId) {
        cleanUrl.searchParams.set('variant', variantId);
      }

      return `${cleanUrl.pathname}${cleanUrl.search}`;
    } catch (error) {
      return `/products/${encodeURIComponent(handle)}`;
    }
  };

  const getNumericResourceId = (value) => String(value || '').split('/').pop();

  const createLinkedColorSwatch = ({
    color,
    handle,
    url,
    isCurrent,
    productId = '',
    variantId = '',
    productTitle = ''
  }) => {
    const swatch = document.createElement('a');
    swatch.className = `product-option-swatch product-option-swatch--linked${isCurrent ? ' is-active' : ''}`;
    swatch.href = getCleanLinkedColorUrl(url, handle);
    swatch.setAttribute('aria-label', `Color: ${color}`);
    swatch.dataset.swatchTooltip = color;
    swatch.dataset.linkedColorHandle = handle;
    swatch.dataset.linkedColorValue = color;
    swatch.dataset.linkedColorDiscovered = 'true';
    swatch.dataset.linkedColorProductId = String(productId || '');
    swatch.dataset.linkedColorVariantId = String(variantId || '');
    swatch.dataset.linkedColorProductTitle = String(productTitle || '');

    if (isCurrent) {
      swatch.setAttribute('aria-current', 'page');
    }

    const visual = document.createElement('span');
    visual.className = 'product-option-swatch__visual';
    visual.dataset.swatchValue = color;

    const swatchStyle = window.WobSwatches?.getSwatchStyle?.(color);
    if (swatchStyle) {
      visual.setAttribute('style', swatchStyle);
    }

    const hiddenLabel = document.createElement('span');
    hiddenLabel.className = 'visually-hidden';
    hiddenLabel.textContent = color;

    swatch.append(visual, hiddenLabel);
    return swatch;
  };

  const hydrateLegacyLinkedColorGroup = async (fieldset) => {
    const swatchContainer = fieldset.querySelector('[data-linked-color-swatches]');
    const productBase = fieldset.dataset.linkedColorBase?.trim();

    if (!swatchContainer || !productBase) return;

    fieldset.setAttribute('aria-busy', 'true');

    const routesRoot = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const endpoint = new URL(`${routesRoot}search/suggest.json`, window.location.origin);
    endpoint.searchParams.set('q', productBase);
    endpoint.searchParams.set('resources[type]', 'product');
    endpoint.searchParams.set('resources[limit]', '10');
    endpoint.searchParams.set('resources[options][unavailable_products]', 'show');
    const workerEndpoint = String(fieldset.dataset.linkedColorWorkerEndpoint || '').replace(/\/$/, '');
    const workerEnabled = fieldset.dataset.linkedColorWorkerEnabled === 'true';
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 4500);
    const syntheticCurrentSwatch = swatchContainer.querySelector('[data-linked-color-synthetic-current]');

    try {
      const predictiveRequest = fetch(endpoint.toString(), {
        headers: { Accept: 'application/json' },
        signal: controller.signal
      })
        .then((response) => response.ok ? response.json() : null)
        .then((payload) => payload?.resources?.results?.products || []);
      const workerRequest = workerEnabled && workerEndpoint
        ? fetch(`${workerEndpoint}/search`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              query: productBase,
              normalizedQuery: normalizeLinkedColorText(productBase),
              productLimit: 50,
              collectionLimit: 0,
              pageSize: 50,
              include: ['products'],
              source: 'product-page-linked-colors'
            }),
            signal: controller.signal
          })
            .then((response) => response.ok ? response.json() : null)
            .then((payload) => payload?.fallback?.recommended ? [] : payload?.products || [])
        : Promise.resolve([]);
      const [predictiveResult, workerResult] = await Promise.allSettled([
        predictiveRequest,
        workerRequest
      ]);
      const products = [
        ...(workerResult.status === 'fulfilled' ? workerResult.value : []),
        ...(predictiveResult.status === 'fulfilled' ? predictiveResult.value : [])
      ];

      if (
        !Array.isArray(products) ||
        fieldset.dataset.linkedColorLoading !== 'true'
      ) {
        return;
      }

      const currentHandle = normalizeLinkedColorText(fieldset.dataset.linkedColorCurrentHandle);
      const existingHandles = new Set();
      const existingColors = new Set();

      syntheticCurrentSwatch?.remove();

      swatchContainer.querySelectorAll('.product-option-swatch--linked').forEach((swatch) => {
        const handle = swatch.dataset.linkedColorHandle || getLinkedColorHandle(swatch.getAttribute('href'));
        const color = swatch.dataset.linkedColorValue;

        if (handle) existingHandles.add(normalizeLinkedColorText(handle));
        if (color) existingColors.add(normalizeLinkedColorText(color));
      });

      products.forEach((candidate) => {
        const titleParts = String(candidate?.title || '').split(' - ');
        const candidateBase = titleParts.shift()?.trim();
        const color = titleParts.join(' - ').trim();
        const handle = String(candidate?.handle || getLinkedColorHandle(candidate?.url)).trim();
        const normalizedHandle = normalizeLinkedColorText(handle);
        const normalizedColor = normalizeLinkedColorText(color);
        const candidateVariants = Array.isArray(candidate?.variants) ? candidate.variants : [];
        const candidateVariant = candidateVariants.find((variant) =>
          !variant?.hidden && (variant?.available ?? variant?.availableForSale)
        ) || candidateVariants.find((variant) => !variant?.hidden) || candidateVariants[0];

        if (
          normalizeLinkedColorText(candidateBase) !== normalizeLinkedColorText(productBase) ||
          !color ||
          !handle ||
          existingHandles.has(normalizedHandle) ||
          existingColors.has(normalizedColor)
        ) {
          return;
        }

        swatchContainer.append(
          createLinkedColorSwatch({
            color,
            handle,
            url: candidate.url,
            isCurrent: normalizedHandle === currentHandle,
            productId: candidate.numericId || getNumericResourceId(candidate.id),
            variantId: candidateVariant?.numericId || getNumericResourceId(
              candidateVariant?.id || candidate?.selectedVariantId
            ),
            productTitle: candidate.title
          })
        );
        existingHandles.add(normalizedHandle);
        existingColors.add(normalizedColor);
      });

      if (syntheticCurrentSwatch && !existingHandles.has(currentHandle)) {
        swatchContainer.append(syntheticCurrentSwatch);
      }

    } catch (error) {
      if (syntheticCurrentSwatch && !syntheticCurrentSwatch.isConnected) {
        swatchContainer.append(syntheticCurrentSwatch);
      }
      // Keep the merchant-configured linked colors when predictive search is unavailable.
    } finally {
      window.clearTimeout(timeoutId);

      try {
        await Promise.resolve(window.WobSwatches?.ready);
      } catch (error) {
        // The shared swatch loader releases theme fallbacks when its lookup fails.
      }

      window.WobSwatches?.refresh?.(fieldset);
      fieldset.querySelector('[data-linked-color-skeleton]')?.remove();
      fieldset.dataset.linkedColorLoading = 'false';
      fieldset.removeAttribute('aria-busy');
    }
  };

  document.querySelectorAll('[data-linked-color-group]').forEach((fieldset) => {
    hydrateLegacyLinkedColorGroup(fieldset);
  });

  productRoots.forEach((root) => {
    const mediaPanels = Array.from(root.querySelectorAll('[data-media-panel]'));
    const mediaTriggers = Array.from(root.querySelectorAll('[data-media-trigger]'));
    const mediaStage = root.querySelector('[data-product-media-stage]');
    const mediaNote = root.querySelector('[data-product-media-note]');
    const mediaNoteText = root.querySelector('[data-product-media-note-text]');
    const thumbnailWrap = root.querySelector('[data-product-thumbnail-wrap]');
    const thumbnailScroller = root.querySelector('[data-product-thumbnail-scroller]');
    const thumbnailScrollbar = root.querySelector('[data-product-thumbnail-scrollbar]');
    const thumbnailScrollbarThumb = root.querySelector('[data-product-thumbnail-scrollbar-thumb]');
    const productInfo = root.querySelector('.product-template__info');
    const mediaPrevButton = root.querySelector('[data-product-media-prev]');
    const mediaNextButton = root.querySelector('[data-product-media-next]');
    const mediaZoomToggle = root.querySelector('[data-product-media-zoom-toggle]');
    const stickyAtc = document.querySelector('[data-product-sticky-atc]');
    const configuredColorOptionIndex = Number(root.dataset.colorOptionIndex);
    const configuredSizeOptionIndex = Number(root.dataset.sizeOptionIndex);
    const isGiftCardProduct = root.dataset.giftCard === 'true';
    const hasMappedColorMedia = mediaPanels.some((panel) => panel.dataset.mediaColors);
    const twoUpQuery = window.matchMedia('(min-width: 1250px), (min-width: 750px) and (max-width: 989px)');
    const sidebarQuery = window.matchMedia('(min-width: 990px)');
    const verticalThumbnailRailQuery = window.matchMedia('(min-width: 750px)');
    let currentAllowedPanels = mediaPanels;
    let currentMediaId = mediaPanels[0]?.dataset.mediaId || '';
    let mediaAnimationTimer;
    let mediaActivationRequest = 0;
    let mediaInitialized = false;
    let mediaCarouselInteracted = false;
    let mediaSwipe = null;
    let mediaZoomDrag = null;
    let mediaPinchZoom = null;
    let lastMediaTap = null;
    let suppressMediaClick = false;
    let thumbnailRailMaxHeight = '';
    const activeMediaPointers = new Map();
    const mediaZoomScale = 2.25;
    const mediaZoomMinScale = 1;
    const mediaZoomMaxScale = 3;

    const toHandle = (value) => {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    };

    const sizeGuideModal = root.querySelector('[data-size-guide-modal]');

    if (sizeGuideModal && sizeGuideModal.parentElement !== document.body) {
      document.body.appendChild(sizeGuideModal);
    }

    const sizeGuideOpenButtons = root.querySelectorAll('[data-size-guide-open]');
    const sizeGuideCloseButtons = sizeGuideModal ? sizeGuideModal.querySelectorAll('[data-size-guide-close]') : [];
    const sizeGuideTabs = sizeGuideModal ? Array.from(sizeGuideModal.querySelectorAll('[data-size-guide-tab]')) : [];
    const sizeGuidePanels = sizeGuideModal ? Array.from(sizeGuideModal.querySelectorAll('[data-size-guide-panel]')) : [];
    const sizeGuideRegionFilter = sizeGuideModal ? sizeGuideModal.querySelector('[data-size-guide-region-filter-wrap]') : null;
    const sizeGuideRegionButtons = sizeGuideModal ? Array.from(sizeGuideModal.querySelectorAll('[data-size-guide-region-filter]')) : [];
    const sizeGuideFocusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    let sizeGuideLastFocusedElement = null;
    let sizeGuideCloseTimer = null;

    const isSizeGuideOpen = () => Boolean(sizeGuideModal && !sizeGuideModal.hidden);

    const getSizeGuideFocusableElements = () => {
      if (!sizeGuideModal) return [];

      return Array.from(sizeGuideModal.querySelectorAll(sizeGuideFocusableSelector)).filter((element) => {
        return element instanceof HTMLElement && !element.hidden && element.offsetParent !== null;
      });
    };

    const getSizeGuideActiveRegion = () => {
      return sizeGuideRegionButtons.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.sizeGuideRegion || 'us';
    };

    const getActiveSizeGuidePanel = () => {
      return sizeGuidePanels.find((panel) => !panel.hidden) || sizeGuidePanels[0] || null;
    };

    const syncSizeGuideCountryColumns = () => {
      if (!sizeGuideModal) return;

      const hasRegionalContent = window.WobSizeGuideTables?.syncRegion(sizeGuideModal, getActiveSizeGuidePanel(), getSizeGuideActiveRegion()) || false;

      if (sizeGuideRegionFilter) {
        sizeGuideRegionFilter.hidden = !hasRegionalContent;
        sizeGuideModal.classList.toggle('product-size-guide-modal--has-region-filter', hasRegionalContent);
      }
    };

    const setActiveSizeGuideRegion = (region, shouldFocus = false) => {
      if (!region) return;

      sizeGuideRegionButtons.forEach((button) => {
        const isSelected = button.dataset.sizeGuideRegion === region;

        button.setAttribute('aria-pressed', String(isSelected));
      });

      syncSizeGuideCountryColumns();

      if (shouldFocus) {
        sizeGuideRegionButtons.find((button) => button.dataset.sizeGuideRegion === region)?.focus({ preventScroll: true });
      }
    };

    const simplifyGeneralSizeGuidePanel = (panel) => {
      if (!(panel instanceof HTMLElement) || panel.dataset.sizeGuidePanel !== 'general' || panel.dataset.sizeGuideGeneralSimplified === 'true') return;

      panel.dataset.sizeGuideGeneralSimplified = 'true';

      const cutoffPatterns = [
        /jean\s+sizing/i,
        /^kancan$/i,
        /^kan\s*can$/i,
        /^risen\s+jeans?$/i,
        /^eunina$/i,
        /^vervet$/i,
        /^flying\s+monkey$/i,
        /^mica\s+jeans?$/i,
        /^just\s+black\s+denim$/i,
        /^hidden\s+jeans?$/i
      ];
      const elements = Array.from(panel.children).filter((element) => element instanceof HTMLElement);
      const cutoff = elements.find((element) => {
        const text = element.textContent?.replace(/\s+/g, ' ').trim() || '';

        return cutoffPatterns.some((pattern) => pattern.test(text));
      });

      if (!cutoff) return;

      let element = cutoff;

      while (element) {
        const nextElement = element.nextElementSibling;

        element.remove();
        element = nextElement;
      }
    };

    const prepareSizeGuideCharts = () => {
      if (!sizeGuideModal) return;

      sizeGuidePanels.forEach((panel) => {
        simplifyGeneralSizeGuidePanel(panel);
        window.WobSizeGuideTables?.prepare(panel);
      });
    };

    const setActiveSizeGuideTab = (tab, shouldFocus = false) => {
      if (!tab) return;

      const target = tab.dataset.sizeGuideTarget;

      sizeGuideTabs.forEach((item) => {
        const isSelected = item === tab;

        item.setAttribute('aria-selected', String(isSelected));
        item.tabIndex = isSelected ? 0 : -1;
      });

      sizeGuidePanels.forEach((panel) => {
        panel.hidden = panel.dataset.sizeGuidePanel !== target;
      });

      syncSizeGuideCountryColumns();

      window.requestAnimationFrame(() => {
        tab.scrollIntoView({ block: 'nearest', inline: 'center' });
      });

      if (shouldFocus) tab.focus({ preventScroll: true });
    };

    const openSizeGuideModal = () => {
      if (!sizeGuideModal) return;

      window.clearTimeout(sizeGuideCloseTimer);
      sizeGuideLastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      sizeGuideModal.hidden = false;
      sizeGuideModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('product-size-guide-is-open');
      document.body.classList.add('product-size-guide-is-open');

      prepareSizeGuideCharts();
      const selectedTab = sizeGuideTabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || sizeGuideTabs[0];

      if (selectedTab) {
        setActiveSizeGuideTab(selectedTab);
      } else {
        syncSizeGuideCountryColumns();
      }

      window.requestAnimationFrame(() => {
        sizeGuideModal.classList.add('product-size-guide-modal--open');
        const focusTarget = selectedTab || sizeGuideModal.querySelector('[data-size-guide-close]') || sizeGuideModal;
        focusTarget.focus({ preventScroll: true });
      });
    };

    const closeSizeGuideModal = ({ restoreFocus = true } = {}) => {
      if (!sizeGuideModal || sizeGuideModal.hidden) return;

      sizeGuideModal.classList.remove('product-size-guide-modal--open');
      sizeGuideModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('product-size-guide-is-open');
      document.body.classList.remove('product-size-guide-is-open');

      sizeGuideCloseTimer = window.setTimeout(() => {
        sizeGuideModal.hidden = true;
      }, 180);

      if (restoreFocus && sizeGuideLastFocusedElement) {
        sizeGuideLastFocusedElement.focus({ preventScroll: true });
      }
    };

    const trapSizeGuideFocus = (event) => {
      if (event.key !== 'Tab' || !isSizeGuideOpen()) return;

      const focusableElements = getSizeGuideFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        sizeGuideModal.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    if (sizeGuideModal) {
      const selectedTab = sizeGuideTabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || sizeGuideTabs[0];
      prepareSizeGuideCharts();

      if (selectedTab) {
        setActiveSizeGuideTab(selectedTab);
      } else {
        syncSizeGuideCountryColumns();
      }

      sizeGuideOpenButtons.forEach((button) => {
        button.addEventListener('click', openSizeGuideModal);
      });

      sizeGuideCloseButtons.forEach((button) => {
        button.addEventListener('click', () => closeSizeGuideModal());
      });

      sizeGuideTabs.forEach((tab, index) => {
        tab.addEventListener('click', () => setActiveSizeGuideTab(tab));
        tab.addEventListener('keydown', (event) => {
          const isForward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
          const isBackward = event.key === 'ArrowLeft' || event.key === 'ArrowUp';

          if (!isForward && !isBackward) return;

          event.preventDefault();
          const direction = isForward ? 1 : -1;
          const nextIndex = (index + direction + sizeGuideTabs.length) % sizeGuideTabs.length;

          setActiveSizeGuideTab(sizeGuideTabs[nextIndex], true);
        });
      });

      sizeGuideRegionButtons.forEach((button, index) => {
        button.addEventListener('click', () => setActiveSizeGuideRegion(button.dataset.sizeGuideRegion));
        button.addEventListener('keydown', (event) => {
          const isForward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
          const isBackward = event.key === 'ArrowLeft' || event.key === 'ArrowUp';

          if (!isForward && !isBackward) return;

          event.preventDefault();
          const direction = isForward ? 1 : -1;
          const nextIndex = (index + direction + sizeGuideRegionButtons.length) % sizeGuideRegionButtons.length;

          setActiveSizeGuideRegion(sizeGuideRegionButtons[nextIndex]?.dataset.sizeGuideRegion, true);
        });
      });

      sizeGuideModal.addEventListener('change', (event) => {
        if (!(event.target instanceof HTMLSelectElement)) return;
        syncSizeGuideCountryColumns();
      });

      document.addEventListener('keydown', (event) => {
        if (!isSizeGuideOpen()) return;

        if (event.key === 'Tab' || event.key === 'Escape') {
          event.stopPropagation();
        }

        trapSizeGuideFocus(event);

        if (event.key === 'Escape') {
          event.preventDefault();
          closeSizeGuideModal();
        }
      }, true);
    }

    // The model popup is rendered by the shared model-directory section group, outside this product root.
    const modelProfileTriggers = Array.from(root.querySelectorAll('[data-model-profile-open]'));
    let modelProfileModal = null;
    let modelProfileLastFocusedElement = null;
    let modelProfileCloseTimer = null;

    const resolveModelProfileModal = () => {
      const productId = modelProfileTriggers[0]?.dataset.modelProfileProduct || '';
      const modals = Array.from(document.querySelectorAll('[data-model-profile-modal]'))
        .filter((modal) => modal.dataset.modelProfileProduct === productId);
      // Theme editor re-renders leave a stale copy behind in <body>; prefer the freshly rendered one.
      const freshModal = modals.find((modal) => modal.parentElement !== document.body) || modals[0] || null;

      modals.forEach((modal) => {
        if (modal !== freshModal && modal.parentElement === document.body) modal.remove();
      });

      if (freshModal && freshModal.parentElement !== document.body) {
        document.body.appendChild(freshModal);
      }

      modelProfileModal = freshModal;
      return freshModal;
    };

    const isModelProfileOpen = () => Boolean(modelProfileModal && modelProfileModal.isConnected && !modelProfileModal.hidden);

    const getModelProfileFocusableElements = () => {
      if (!modelProfileModal) return [];

      return Array.from(modelProfileModal.querySelectorAll(sizeGuideFocusableSelector)).filter((element) => {
        return element instanceof HTMLElement && !element.hidden && element.offsetParent !== null;
      });
    };

    const openModelProfileModal = () => {
      if (!resolveModelProfileModal()) return false;

      window.clearTimeout(modelProfileCloseTimer);
      modelProfileLastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      modelProfileModal.hidden = false;
      modelProfileModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('product-model-modal-is-open');
      document.body.classList.add('product-model-modal-is-open');

      window.requestAnimationFrame(() => {
        modelProfileModal.classList.add('product-model-modal--open');
        const focusTarget = modelProfileModal.querySelector('.product-model-modal__close') || modelProfileModal;
        focusTarget.focus({ preventScroll: true });
      });

      return true;
    };

    const closeModelProfileModal = ({ restoreFocus = true } = {}) => {
      if (!modelProfileModal || modelProfileModal.hidden) return;

      const closingModal = modelProfileModal;
      closingModal.classList.remove('product-model-modal--open');
      closingModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('product-model-modal-is-open');
      document.body.classList.remove('product-model-modal-is-open');

      modelProfileCloseTimer = window.setTimeout(() => {
        closingModal.hidden = true;
      }, 180);

      if (restoreFocus && modelProfileLastFocusedElement) {
        modelProfileLastFocusedElement.focus({ preventScroll: true });
      }
    };

    const trapModelProfileFocus = (event) => {
      if (event.key !== 'Tab' || !isModelProfileOpen()) return;

      const focusableElements = getModelProfileFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        modelProfileModal.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    if (modelProfileTriggers.length && resolveModelProfileModal()) {
      modelProfileTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', modelProfileModal.id);

        // Model triggers without a linked page render as a div; upgrade them to a keyboard button.
        if (trigger.tagName !== 'A') {
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('tabindex', '0');
          trigger.classList.add('product-model-info--link');
          trigger.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            openModelProfileModal();
          });
        }

        trigger.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
          if (openModelProfileModal()) event.preventDefault();
        });
      });

      document.addEventListener('click', (event) => {
        const closeControl = event.target instanceof Element ? event.target.closest('[data-model-profile-close]') : null;
        if (closeControl && modelProfileModal && modelProfileModal.contains(closeControl)) {
          closeModelProfileModal();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (!isModelProfileOpen()) return;

        if (event.key === 'Tab' || event.key === 'Escape') {
          event.stopPropagation();
        }

        trapModelProfileFocus(event);

        if (event.key === 'Escape') {
          event.preventDefault();
          closeModelProfileModal();
        }
      }, true);

      // Let merchants preview popup edits when selecting a model block in the theme editor.
      document.addEventListener('shopify:section:load', () => {
        if (isModelProfileOpen()) closeModelProfileModal({ restoreFocus: false });
        resolveModelProfileModal();
      });

      document.addEventListener('shopify:block:select', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[data-model-profile-modal]')) return;
        openModelProfileModal();
      });
    }

    const getMediaColors = (element) => {
      return (element.dataset.mediaColors || '').split('|').filter(Boolean);
    };

    const loadDeferredImages = (element, { eager = false } = {}) => {
      if (!element) return;

      element.querySelectorAll('img[data-src], img[data-srcset]').forEach((image) => {
        if (eager) {
          image.loading = 'eager';
        }

        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        }

        if (image.dataset.src) {
          image.src = image.dataset.src;
          image.removeAttribute('data-src');
        }
      });
    };

    const loadCurrentVariantFullSizeImages = () => {
      mediaCarouselInteracted = true;
      currentAllowedPanels.forEach((panel) => loadDeferredImages(panel, { eager: true }));
    };

    const isThumbnailRailVertical = () => verticalThumbnailRailQuery.matches;

    const updateThumbnailRailHeight = () => {
      if (!thumbnailWrap) return;

      if (!isThumbnailRailVertical()) {
        thumbnailRailMaxHeight = '';
        root.style.removeProperty('--product-thumbnail-rail-max-height');
        return;
      }

      const activePanel = mediaPanels.find((panel) => {
        return panel.classList.contains('product-media--active') && !panel.hidden;
      }) || mediaPanels.find((panel) => !panel.hidden);
      const height = activePanel?.getBoundingClientRect().height || mediaStage?.getBoundingClientRect().height || 0;

      if (height <= 0) return;

      const nextHeight = `${Math.round(height)}px`;

      if (thumbnailRailMaxHeight === nextHeight) return;

      thumbnailRailMaxHeight = nextHeight;
      root.style.setProperty('--product-thumbnail-rail-max-height', nextHeight);
    };

    const updateThumbnailScrollbar = () => {
      if (!thumbnailWrap || !thumbnailScroller || !thumbnailScrollbar || !thumbnailScrollbarThumb) return;

      updateThumbnailRailHeight();

      const isVertical = isThumbnailRailVertical();
      const scrollSize = isVertical ? thumbnailScroller.scrollHeight : thumbnailScroller.scrollWidth;
      const clientSize = isVertical ? thumbnailScroller.clientHeight : thumbnailScroller.clientWidth;
      const scrollPosition = isVertical ? thumbnailScroller.scrollTop : thumbnailScroller.scrollLeft;
      const isScrollable = scrollSize > clientSize + 1;

      thumbnailWrap.classList.toggle('product-media-thumbnails-wrap--scrollable', isScrollable);

      if (!isScrollable) {
        thumbnailScrollbarThumb.style.setProperty('--product-media-scrollbar-thumb-size', '100%');
        thumbnailScrollbarThumb.style.setProperty('--product-media-scrollbar-thumb-offset', '0px');
        return;
      }

      const thumbSize = Math.max((clientSize / scrollSize) * clientSize, 24);
      const maxThumbOffset = Math.max(clientSize - thumbSize, 0);
      const maxScroll = Math.max(scrollSize - clientSize, 1);
      const thumbOffset = (scrollPosition / maxScroll) * maxThumbOffset;

      thumbnailScrollbarThumb.style.setProperty('--product-media-scrollbar-thumb-size', `${thumbSize}px`);
      thumbnailScrollbarThumb.style.setProperty('--product-media-scrollbar-thumb-offset', `${thumbOffset}px`);
    };

    const syncThumbnailRail = () => {
      if (!thumbnailScroller) return;

      const activeTrigger = mediaTriggers.find((trigger) => {
        return trigger.getAttribute('aria-current') === 'true' && !trigger.hidden;
      });

      if (activeTrigger) {
        const isVertical = isThumbnailRailVertical();
        const scrollerRect = thumbnailScroller.getBoundingClientRect();
        const triggerRect = activeTrigger.getBoundingClientRect();

        if (isVertical) {
          const currentScroll = thumbnailScroller.scrollTop;
          const triggerStart = triggerRect.top - scrollerRect.top + currentScroll;
          const triggerEnd = triggerStart + triggerRect.height;
          const visibleEnd = currentScroll + thumbnailScroller.clientHeight;

          if (triggerStart < currentScroll) {
            thumbnailScroller.scrollTop = triggerStart;
          } else if (triggerEnd > visibleEnd) {
            thumbnailScroller.scrollTop = triggerEnd - thumbnailScroller.clientHeight;
          }
        } else {
          const currentScroll = thumbnailScroller.scrollLeft;
          const triggerStart = triggerRect.left - scrollerRect.left + currentScroll;
          const triggerEnd = triggerStart + triggerRect.width;
          const visibleEnd = currentScroll + thumbnailScroller.clientWidth;

          if (triggerStart < currentScroll) {
            thumbnailScroller.scrollLeft = triggerStart;
          } else if (triggerEnd > visibleEnd) {
            thumbnailScroller.scrollLeft = triggerEnd - thumbnailScroller.clientWidth;
          }
        }
      }

      window.requestAnimationFrame(updateThumbnailScrollbar);
    };

    const getVisibleMediaPanels = (activePanel, allowedPanels) => {
      if (!activePanel) return [];
      if (!twoUpQuery.matches) return [activePanel];

      const activeIndex = allowedPanels.findIndex((panel) => panel === activePanel);
      const secondaryPanel = allowedPanels.find((panel, index) => {
        return index > activeIndex && panel !== activePanel;
      }) || allowedPanels.find((panel) => panel !== activePanel);

      return secondaryPanel ? [activePanel, secondaryPanel] : [activePanel];
    };

    const updateProductInfoHeight = () => {
      if (!productInfo || !mediaStage) return;

      if (!sidebarQuery.matches || isGiftCardProduct) {
        root.style.removeProperty('--product-info-height');
        return;
      }

      const height = mediaStage.getBoundingClientRect().height;

      if (height > 0) {
        root.style.setProperty('--product-info-height', `${height}px`);
      }
    };

    const getZoomPanel = (target) => {
      if (!(target instanceof Element)) return null;

      return target.closest('[data-media-panel].product-media--zoomable');
    };

    const getVisibleZoomPanels = () => {
      return mediaPanels.filter((panel) => {
        return !panel.hidden && panel.classList.contains('product-media--zoomable');
      });
    };

    const getZoomTogglePanel = () => {
      const visibleZoomPanels = getVisibleZoomPanels();

      return visibleZoomPanels.find((panel) => panel.classList.contains('product-media--zoomed'))
        || visibleZoomPanels.find((panel) => getZoomPanel(document.activeElement) === panel)
        || (twoUpQuery.matches && visibleZoomPanels.length > 1 ? visibleZoomPanels[visibleZoomPanels.length - 1] : null)
        || visibleZoomPanels.find((panel) => panel.dataset.mediaId === currentMediaId)
        || visibleZoomPanels.find((panel) => panel.classList.contains('product-media--active'))
        || visibleZoomPanels[0]
        || null;
    };

    const getZoomImage = (panel) => {
      return panel?.querySelector('[data-product-media-zoom-image]');
    };

    const getZoomPosition = (panel) => {
      return {
        x: Number(panel?.dataset.zoomX) || 0,
        y: Number(panel?.dataset.zoomY) || 0
      };
    };

    const getZoomScale = (panel) => {
      return Number(panel?.dataset.zoomScale) || mediaZoomMinScale;
    };

    const clampZoomScale = (scale) => {
      return Math.min(Math.max(scale, mediaZoomMinScale), mediaZoomMaxScale);
    };

    const getZoomPointPosition = (panel, point, scale) => {
      const rect = panel.getBoundingClientRect();

      return {
        x: -(point.x - rect.left - rect.width / 2) * (scale - 1),
        y: -(point.y - rect.top - rect.height / 2) * (scale - 1)
      };
    };

    const clampZoomPosition = (panel, x, y) => {
      const rect = panel.getBoundingClientRect();
      const scale = getZoomScale(panel);
      const maxX = Math.max((rect.width * (scale - 1)) / 2, 0);
      const maxY = Math.max((rect.height * (scale - 1)) / 2, 0);

      return {
        x: Math.min(Math.max(x, -maxX), maxX),
        y: Math.min(Math.max(y, -maxY), maxY)
      };
    };

    const setZoomPosition = (panel, x, y) => {
      const image = getZoomImage(panel);
      const position = clampZoomPosition(panel, x, y);

      panel.dataset.zoomX = String(position.x);
      panel.dataset.zoomY = String(position.y);
      image?.style.setProperty('--product-media-zoom-x', `${position.x}px`);
      image?.style.setProperty('--product-media-zoom-y', `${position.y}px`);
    };

    const setZoomScale = (panel, scale) => {
      const image = getZoomImage(panel);
      const nextScale = clampZoomScale(scale);

      panel.dataset.zoomScale = String(nextScale);
      image?.style.setProperty('--product-media-zoom-scale', String(nextScale));
    };

    const setZoomPanelState = (panel, isZoomed) => {
      panel.setAttribute('aria-pressed', isZoomed ? 'true' : 'false');
      panel.setAttribute('aria-label', isZoomed ? 'Drag to pan image. Double tap or press Escape to reset zoom.' : 'Zoom and drag image');
    };

    const updateZoomingStageState = () => {
      const isZoomed = Boolean(root.querySelector('.product-media--zoomed:not([hidden])'));

      mediaStage?.classList.toggle('product-media-stage--zooming', isZoomed);

      if (mediaZoomToggle) {
        mediaZoomToggle.setAttribute('aria-pressed', isZoomed ? 'true' : 'false');
        mediaZoomToggle.setAttribute('aria-label', isZoomed ? 'Zoom out product image' : 'Zoom product image');
      }
    };

    const resetMediaZoom = (panel) => {
      if (!panel) return;

      const image = getZoomImage(panel);

      panel.classList.remove('product-media--zoomed', 'product-media--zoom-dragging');
      delete panel.dataset.zoomScale;
      delete panel.dataset.zoomX;
      delete panel.dataset.zoomY;
      image?.style.removeProperty('--product-media-zoom-scale');
      image?.style.removeProperty('--product-media-zoom-x');
      image?.style.removeProperty('--product-media-zoom-y');
      setZoomPanelState(panel, false);

      if (mediaZoomDrag?.panel === panel) {
        mediaZoomDrag = null;
      }

      if (mediaPinchZoom?.panel === panel) {
        mediaPinchZoom = null;
      }

      updateZoomingStageState();
    };

    const resetAllMediaZoom = () => {
      mediaPanels.forEach((panel) => resetMediaZoom(panel));
    };

    const enableMediaZoom = (panel, point = null, scale = mediaZoomScale) => {
      const image = getZoomImage(panel);
      if (!image) return;

      const nextScale = clampZoomScale(scale);
      let position = { x: 0, y: 0 };

      panel.classList.add('product-media--zoomed');
      setZoomScale(panel, nextScale);

      if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
        position = getZoomPointPosition(panel, point, nextScale);
      }

      setZoomPosition(panel, position.x, position.y);
      setZoomPanelState(panel, true);
      updateZoomingStageState();
    };

    const getPanelPointers = (panel) => {
      return Array.from(activeMediaPointers.values()).filter((pointer) => pointer.panel === panel);
    };

    const getPointerDistance = (pointerA, pointerB) => {
      return Math.hypot(pointerA.x - pointerB.x, pointerA.y - pointerB.y);
    };

    const getPointerCenter = (pointerA, pointerB) => {
      return {
        x: (pointerA.x + pointerB.x) / 2,
        y: (pointerA.y + pointerB.y) / 2
      };
    };

    const startMediaPinchZoom = (panel) => {
      const pointers = getPanelPointers(panel);
      if (pointers.length < 2) return;

      const [pointerA, pointerB] = pointers;
      const distance = getPointerDistance(pointerA, pointerB);
      if (distance <= 0) return;

      const scale = panel.classList.contains('product-media--zoomed') ? getZoomScale(panel) : mediaZoomMinScale;

      panel.classList.add('product-media--zoomed');
      setZoomScale(panel, scale);
      setZoomPanelState(panel, true);
      updateZoomingStageState();
      resetMediaSwipe();
      mediaZoomDrag = null;

      mediaPinchZoom = {
        panel,
        pointerIds: [pointerA.id, pointerB.id],
        startDistance: distance,
        startScale: scale
      };
    };

    const startMediaZoomDrag = (event) => {
      const panel = getZoomPanel(event.target);

      if (!panel?.classList.contains('product-media--zoomed')) return;
      if (mediaPinchZoom) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.pointerType === 'touch' && getPanelPointers(panel).length > 1) return;

      const position = getZoomPosition(panel);

      mediaZoomDrag = {
        panel,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: position.x,
        originY: position.y,
        moved: false
      };

      panel.classList.add('product-media--zoom-dragging');
      event.preventDefault();

      try {
        panel.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture can fail for synthetic events; dragging still works without it.
      }
    };

    const moveMediaZoomDrag = (event) => {
      if (!mediaZoomDrag || mediaZoomDrag.pointerId !== event.pointerId) return;

      event.preventDefault();
      const deltaX = event.clientX - mediaZoomDrag.startX;
      const deltaY = event.clientY - mediaZoomDrag.startY;

      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        mediaZoomDrag.moved = true;
        suppressMediaClick = true;
      }

      setZoomPosition(
        mediaZoomDrag.panel,
        mediaZoomDrag.originX + deltaX,
        mediaZoomDrag.originY + deltaY
      );
    };

    const endMediaZoomDrag = (event) => {
      if (!mediaZoomDrag || mediaZoomDrag.pointerId !== event.pointerId) return;

      mediaZoomDrag.panel.classList.remove('product-media--zoom-dragging');
      mediaZoomDrag = null;
    };

    const trackMediaPointerDown = (event) => {
      const panel = getZoomPanel(event.target);
      if (!panel || event.pointerType !== 'touch') return;

      activeMediaPointers.set(event.pointerId, {
        id: event.pointerId,
        panel,
        x: event.clientX,
        y: event.clientY,
        startX: event.clientX,
        startY: event.clientY,
        startedAt: Date.now()
      });

      if (getPanelPointers(panel).length >= 2) {
        event.preventDefault();
        startMediaPinchZoom(panel);
      }
    };

    const trackMediaPointerMove = (event) => {
      const pointer = activeMediaPointers.get(event.pointerId);
      if (!pointer) return;

      pointer.x = event.clientX;
      pointer.y = event.clientY;

      if (!mediaPinchZoom || !mediaPinchZoom.pointerIds.includes(event.pointerId)) return;

      const [pointerA, pointerB] = mediaPinchZoom.pointerIds.map((id) => activeMediaPointers.get(id));
      if (!pointerA || !pointerB) return;

      const distance = getPointerDistance(pointerA, pointerB);
      const scale = clampZoomScale(mediaPinchZoom.startScale * (distance / mediaPinchZoom.startDistance));
      const center = getPointerCenter(pointerA, pointerB);
      const position = getZoomPointPosition(mediaPinchZoom.panel, center, scale);

      event.preventDefault();
      setZoomScale(mediaPinchZoom.panel, scale);
      setZoomPosition(mediaPinchZoom.panel, position.x, position.y);
    };

    const trackMediaPointerEnd = (event) => {
      const pointer = activeMediaPointers.get(event.pointerId);
      const activePinchPanel = mediaPinchZoom?.panel;

      if (pointer && event.pointerType === 'touch' && pointer.panel.classList.contains('product-media--zoomed')) {
        const moved = Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY);
        const now = Date.now();

        if (moved < 12 && (!mediaPinchZoom || !mediaPinchZoom.pointerIds.includes(event.pointerId))) {
          if (
            lastMediaTap?.panel === pointer.panel &&
            now - lastMediaTap.time < 340 &&
            Math.hypot(event.clientX - lastMediaTap.x, event.clientY - lastMediaTap.y) < 44
          ) {
            resetMediaZoom(pointer.panel);
            lastMediaTap = null;
          } else {
            lastMediaTap = {
              panel: pointer.panel,
              time: now,
              x: event.clientX,
              y: event.clientY
            };
          }
        }
      }

      activeMediaPointers.delete(event.pointerId);

      if (mediaPinchZoom?.pointerIds.includes(event.pointerId)) {
        const panel = mediaPinchZoom.panel;

        mediaPinchZoom = null;

        if (getZoomScale(panel) <= 1.04) {
          resetMediaZoom(panel);
        }
      }

      if (activePinchPanel && getPanelPointers(activePinchPanel).length < 2) {
        mediaPinchZoom = null;
      }
    };

    const getMediaDirection = (fromMediaId, toMediaId, allowedPanels) => {
      if (!fromMediaId || !toMediaId || fromMediaId === toMediaId || allowedPanels.length <= 1) return '';

      const fromIndex = allowedPanels.findIndex((panel) => panel.dataset.mediaId === fromMediaId);
      const toIndex = allowedPanels.findIndex((panel) => panel.dataset.mediaId === toMediaId);

      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return '';
      if (fromIndex === allowedPanels.length - 1 && toIndex === 0) return 'next';
      if (fromIndex === 0 && toIndex === allowedPanels.length - 1) return 'prev';

      return toIndex > fromIndex ? 'next' : 'prev';
    };

    const clearMediaAnimationClasses = () => {
      mediaStage?.classList.remove('product-media-stage--animating');
      mediaPanels.forEach((panel) => {
        panel.classList.remove(
          'product-media--slide-in-next',
          'product-media--slide-in-prev',
          'product-media--slide-settled'
        );
      });
    };

    const animateMediaPanels = (visiblePanels, direction) => {
      if (!mediaStage || !direction || !visiblePanels.length) return;

      window.clearTimeout(mediaAnimationTimer);
      clearMediaAnimationClasses();

      mediaStage.classList.add('product-media-stage--animating');
      visiblePanels.forEach((panel) => {
        panel.classList.add(`product-media--slide-in-${direction}`);
      });

      mediaStage.offsetWidth;

      visiblePanels.forEach((panel) => {
        panel.classList.add('product-media--slide-settled');
      });

      mediaAnimationTimer = window.setTimeout(clearMediaAnimationClasses, 460);
    };

    const waitForMediaPaint = () => {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });
    };

    const waitForImageLoad = (image) => {
      if (image.complete) {
        return Promise.resolve(image.naturalWidth > 0);
      }

      return new Promise((resolve) => {
        let isSettled = false;
        let timeoutId;

        const settle = (isReady) => {
          if (isSettled) return;
          isSettled = true;
          window.clearTimeout(timeoutId);
          image.removeEventListener('load', handleLoad);
          image.removeEventListener('error', handleError);
          resolve(isReady);
        };

        const handleLoad = () => settle(image.naturalWidth > 0);
        const handleError = () => settle(false);

        image.addEventListener('load', handleLoad, { once: true });
        image.addEventListener('error', handleError, { once: true });
        timeoutId = window.setTimeout(() => settle(false), 8000);

        if (image.complete) {
          settle(image.naturalWidth > 0);
        }
      });
    };

    const waitForImageDecode = async (image) => {
      if (typeof image.decode !== 'function') return true;

      let decodePromise;
      let timeoutId;

      try {
        decodePromise = image.decode();
      } catch (error) {
        return image.complete && image.naturalWidth > 0;
      }

      const decodeTimeout = new Promise((resolve) => {
        timeoutId = window.setTimeout(() => resolve(false), 8000);
      });

      const didDecode = await Promise.race([
        decodePromise
          .then(() => true)
          .catch(() => image.complete && image.naturalWidth > 0),
        decodeTimeout
      ]);

      window.clearTimeout(timeoutId);
      return didDecode;
    };

    const waitForMediaPanel = async (panel) => {
      const image = panel?.querySelector('img[data-product-image]');

      loadDeferredImages(panel, { eager: true });

      if (!image) return true;

      const didLoad = await waitForImageLoad(image);

      if (!didLoad) return false;

      const didDecode = await waitForImageDecode(image);

      if (!didDecode) return false;

      await waitForMediaPaint();

      return image.complete && image.naturalWidth > 0;
    };

    const activateMediaWhenReady = (mediaId, allowedPanels = currentAllowedPanels, direction = '') => {
      const id = String(mediaId || '');
      const activePanel = mediaPanels.find((panel) => panel.dataset.mediaId === id);

      if (!activePanel) return;

      const nextAllowedPanels = allowedPanels.length ? allowedPanels : mediaPanels;
      const visiblePanels = getVisibleMediaPanels(activePanel, nextAllowedPanels);
      const requestId = ++mediaActivationRequest;

      Promise.all(visiblePanels.map(waitForMediaPanel)).then((readyPanels) => {
        if (requestId !== mediaActivationRequest) return;
        if (readyPanels.some((isReady) => !isReady)) return;
        activateMedia(id, nextAllowedPanels, direction);
      }).catch(() => {
        // Keep the current media visible if a browser cannot prepare the requested image.
      });
    };

    const updateCarouselButtons = () => {
      const hasMultipleMedia = currentAllowedPanels.length > 1;

      [mediaPrevButton, mediaNextButton].forEach((button) => {
        if (!button) return;

        button.hidden = !hasMultipleMedia;
        button.disabled = !hasMultipleMedia;
      });
    };

    const navigateMedia = (direction) => {
      loadCurrentVariantFullSizeImages();

      const allowedPanels = currentAllowedPanels.length ? currentAllowedPanels : mediaPanels;

      if (allowedPanels.length <= 1) return;

      let currentIndex = allowedPanels.findIndex((panel) => panel.dataset.mediaId === currentMediaId);
      if (currentIndex < 0) currentIndex = 0;

      const nextIndex = (currentIndex + direction + allowedPanels.length) % allowedPanels.length;
      activateMediaWhenReady(
        allowedPanels[nextIndex].dataset.mediaId,
        allowedPanels,
        direction > 0 ? 'next' : 'prev'
      );
    };

    const resetMediaSwipe = () => {
      mediaSwipe = null;
    };

    const startMediaSwipe = (event) => {
      if (!mediaStage || twoUpQuery.matches || currentAllowedPanels.length <= 1) return;
      if (mediaPinchZoom) return;
      if (event.target instanceof Element && event.target.closest('.product-media--zoomed')) return;
      if (event.pointerType === 'mouse' || event.button !== 0) return;
      if (event.pointerType === 'touch' && getPanelPointers(getZoomPanel(event.target)).length > 1) return;

      mediaSwipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY
      };

      try {
        mediaStage.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture can fail for synthetic events; swipe still works without it.
      }
    };

    const endMediaSwipe = (event) => {
      if (!mediaSwipe || mediaSwipe.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - mediaSwipe.startX;
      const deltaY = event.clientY - mediaSwipe.startY;
      const isHorizontalSwipe = Math.abs(deltaX) >= 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25;

      resetMediaSwipe();

      if (!isHorizontalSwipe) return;

      event.preventDefault();
      navigateMedia(deltaX < 0 ? 1 : -1);
    };

    const activateMedia = (mediaId, allowedPanels = currentAllowedPanels, direction = '') => {
      const id = String(mediaId || '');
      const activePanel = mediaPanels.find((panel) => panel.dataset.mediaId === id);
      const activeTrigger = mediaTriggers.find((trigger) => trigger.dataset.mediaTrigger === id);

      if (!activePanel) return;

      resetAllMediaZoom();

      const nextAllowedPanels = allowedPanels.length ? allowedPanels : mediaPanels;
      const transitionDirection = direction === 'none' ? '' : direction || getMediaDirection(currentMediaId, id, nextAllowedPanels);

      currentMediaId = id;
      currentAllowedPanels = nextAllowedPanels;

      const visiblePanels = getVisibleMediaPanels(activePanel, currentAllowedPanels);
      const visiblePanelIds = new Set(visiblePanels.map((panel) => panel.dataset.mediaId));

      loadDeferredImages(activeTrigger);
      visiblePanels.forEach((panel) => loadDeferredImages(panel));

      mediaPanels.forEach((panel) => {
        const isVisible = visiblePanelIds.has(panel.dataset.mediaId);
        const isActive = panel.dataset.mediaId === id;
        panel.hidden = !isVisible;
        panel.classList.toggle('product-media--active', isActive);
        panel.classList.toggle('product-media--secondary', isVisible && !isActive);
        if (panel.classList.contains('product-media--zoomable')) {
          panel.tabIndex = isVisible ? 0 : -1;
        }
      });

      mediaTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-current', trigger.dataset.mediaTrigger === id ? 'true' : 'false');
      });

      updateCarouselButtons();
      animateMediaPanels(visiblePanels, transitionDirection);
      updateThumbnailRailHeight();
      updateProductInfoHeight();
      syncThumbnailRail();
    };

    mediaStage?.addEventListener('click', (event) => {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (suppressMediaClick) {
        suppressMediaClick = false;
        return;
      }
      if (event.target instanceof Element && event.target.closest('button')) return;

      const panel = getZoomPanel(event.target);
      if (!panel || panel.classList.contains('product-media--zoomed')) return;

      event.preventDefault();
      enableMediaZoom(panel, { x: event.clientX, y: event.clientY });
    });

    mediaStage?.addEventListener('dblclick', (event) => {
      if (event.target instanceof Element && event.target.closest('button')) return;

      const panel = getZoomPanel(event.target);
      if (!panel?.classList.contains('product-media--zoomed')) return;

      event.preventDefault();
      resetMediaZoom(panel);
    });

    mediaStage?.addEventListener('pointerdown', trackMediaPointerDown);
    mediaStage?.addEventListener('pointerdown', startMediaZoomDrag);
    window.addEventListener('pointermove', trackMediaPointerMove, { passive: false });
    window.addEventListener('pointermove', moveMediaZoomDrag, { passive: false });
    window.addEventListener('pointerup', trackMediaPointerEnd);
    window.addEventListener('pointerup', endMediaZoomDrag);
    window.addEventListener('pointercancel', trackMediaPointerEnd);
    window.addEventListener('pointercancel', endMediaZoomDrag);

    root.addEventListener('keydown', (event) => {
      const focusedPanel = getZoomPanel(document.activeElement);

      if ((event.key === 'Enter' || event.key === ' ') && focusedPanel) {
        event.preventDefault();
        loadCurrentVariantFullSizeImages();

        if (focusedPanel.classList.contains('product-media--zoomed')) {
          resetMediaZoom(focusedPanel);
        } else {
          enableMediaZoom(focusedPanel);
        }

        return;
      }

      if (event.key !== 'Escape' || !root.querySelector('.product-media--zoomed')) return;

      event.preventDefault();
      resetAllMediaZoom();
    });

    mediaPrevButton?.addEventListener('click', () => navigateMedia(-1));
    mediaNextButton?.addEventListener('click', () => navigateMedia(1));
    mediaZoomToggle?.addEventListener('click', () => {
      loadCurrentVariantFullSizeImages();

      const activePanel = getZoomTogglePanel();

      if (!activePanel) return;

      if (activePanel.classList.contains('product-media--zoomed')) {
        resetMediaZoom(activePanel);
      } else {
        enableMediaZoom(activePanel);
      }
    });

    mediaStage?.addEventListener('pointerdown', loadCurrentVariantFullSizeImages, { once: true, passive: true });
    mediaStage?.addEventListener('pointerdown', startMediaSwipe);
    mediaStage?.addEventListener('pointerup', endMediaSwipe);
    mediaStage?.addEventListener('pointercancel', resetMediaSwipe);
    mediaStage?.addEventListener('lostpointercapture', resetMediaSwipe);

    mediaTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        loadCurrentVariantFullSizeImages();
        activateMediaWhenReady(trigger.dataset.mediaTrigger, currentAllowedPanels);
      });
    });

    if (thumbnailScroller) {
      thumbnailScroller.addEventListener('scroll', updateThumbnailScrollbar, { passive: true });
      updateThumbnailScrollbar();
    }

    window.addEventListener('resize', () => {
      updateThumbnailRailHeight();
      updateThumbnailScrollbar();
      updateProductInfoHeight();
      root.querySelectorAll('.product-media--zoomed').forEach((panel) => {
        const position = getZoomPosition(panel);
        setZoomPosition(panel, position.x, position.y);
      });
    });

    mediaStage?.querySelectorAll('img').forEach((image) => {
      image.addEventListener('load', () => {
        updateThumbnailRailHeight();
        updateThumbnailScrollbar();
        updateProductInfoHeight();
      });
    });

    if (mediaStage && 'ResizeObserver' in window) {
      const mediaStageResizeObserver = new ResizeObserver(() => {
        updateThumbnailRailHeight();
        updateThumbnailScrollbar();
        updateProductInfoHeight();
      });

      mediaStageResizeObserver.observe(mediaStage);
      mediaPanels.forEach((panel) => mediaStageResizeObserver.observe(panel));
    }

    updateThumbnailRailHeight();
    updateProductInfoHeight();

    const syncActiveMediaLayout = () => {
      if (currentMediaId) activateMediaWhenReady(currentMediaId, currentAllowedPanels, 'none');
      window.requestAnimationFrame(updateThumbnailScrollbar);
      window.requestAnimationFrame(updateProductInfoHeight);
    };

    [twoUpQuery, sidebarQuery, verticalThumbnailRailQuery].forEach((mediaQuery) => {
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', syncActiveMediaLayout);
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(syncActiveMediaLayout);
      }
    });

    if (productInfo) {
      let productInfoTouchY = null;
      let productInfoTouchStartX = null;
      let productInfoTouchStartY = null;

      const routeProductInfoScroll = (deltaY) => {
        const maxScrollTop = productInfo.scrollHeight - productInfo.clientHeight;
        if (maxScrollTop <= 1) return false;

        const currentScrollTop = productInfo.scrollTop;
        const nextScrollTop = Math.min(Math.max(currentScrollTop + deltaY, 0), maxScrollTop);
        const consumedDelta = nextScrollTop - currentScrollTop;
        const remainingDelta = deltaY - consumedDelta;

        if (Math.abs(consumedDelta) >= 0.5) {
          productInfo.scrollTop = nextScrollTop;
        }

        if (Math.abs(remainingDelta) >= 0.5) {
          window.scrollBy({
            top: remainingDelta,
            behavior: 'auto'
          });
        }

        return Math.abs(consumedDelta) >= 0.5 || Math.abs(remainingDelta) >= 0.5;
      };

      root.addEventListener('wheel', (event) => {
        if (isSizeGuideOpen() || isModelProfileOpen()) return;
        if (!sidebarQuery.matches || event.ctrlKey) return;
        if (event.target.closest('[data-product-thumbnail-scroller]')) return;
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

        let deltaY = event.deltaY;
        if (event.deltaMode === 1) deltaY *= 16;
        if (event.deltaMode === 2) deltaY *= productInfo.clientHeight;

        if (routeProductInfoScroll(deltaY)) {
          event.preventDefault();
        }
      }, { passive: false });

      productInfo.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) {
          productInfoTouchY = null;
          productInfoTouchStartX = null;
          productInfoTouchStartY = null;
          return;
        }

        const touch = event.touches[0];
        productInfoTouchY = touch.clientY;
        productInfoTouchStartX = touch.clientX;
        productInfoTouchStartY = touch.clientY;
      }, { passive: true });

      productInfo.addEventListener('touchmove', (event) => {
        if (isSizeGuideOpen() || isModelProfileOpen()) return;
        if (!sidebarQuery.matches) return;
        if (event.touches.length !== 1 || productInfoTouchY === null) return;

        const touch = event.touches[0];
        const totalDeltaX = Math.abs(touch.clientX - productInfoTouchStartX);
        const totalDeltaY = Math.abs(touch.clientY - productInfoTouchStartY);

        if (totalDeltaX > totalDeltaY) {
          productInfoTouchY = touch.clientY;
          return;
        }

        const deltaY = productInfoTouchY - touch.clientY;
        productInfoTouchY = touch.clientY;

        if (Math.abs(deltaY) < 0.5) return;
        if (!event.cancelable) return;

        if (routeProductInfoScroll(deltaY)) {
          event.preventDefault();
        }
      }, { passive: false });

      ['touchend', 'touchcancel'].forEach((eventName) => {
        productInfo.addEventListener(eventName, () => {
          productInfoTouchY = null;
          productInfoTouchStartX = null;
          productInfoTouchStartY = null;
        }, { passive: true });
      });
    }

    const productJson = root.querySelector('[data-product-json]');
    const hiddenVariantJson = root.querySelector('[data-product-hidden-variants]');
    const form = root.querySelector('form[action*="/cart/add"]');
    const select = form?.querySelector('[data-variant-select]');

    if (!productJson || !form || !select) return;

    let product;

    try {
      product = JSON.parse(productJson.textContent);
    } catch (error) {
      return;
    }

    product.options = Array.isArray(product.options) ? product.options : [];
    product.variants = Array.isArray(product.variants) ? product.variants : [];

    const hiddenVariantIds = new Set();

    if (hiddenVariantJson) {
      try {
        const parsedHiddenVariantIds = JSON.parse(hiddenVariantJson.textContent);

        if (Array.isArray(parsedHiddenVariantIds)) {
          parsedHiddenVariantIds.forEach((id) => hiddenVariantIds.add(String(id)));
        }
      } catch (error) {
        // Keep the Shopify product JSON usable if the optional hidden-variant payload is malformed.
      }
    }

    product.variants.forEach((variant) => {
      variant.hidden = hiddenVariantIds.has(String(variant.id));
    });

    const visibleVariants = product.variants.filter((variant) => !variant.hidden);

    const priceContainers = [
      ...root.querySelectorAll('[data-price]'),
      ...Array.from(stickyAtc?.querySelectorAll('[data-price]') || [])
    ];
    const addButton = root.querySelector('[data-add-to-cart]');
    const addText = root.querySelector('[data-add-to-cart-text]');
    const stickyAddButton = stickyAtc?.querySelector('[data-sticky-add-to-cart]');
    const stickyAddText = stickyAtc?.querySelector('[data-sticky-add-to-cart-text]');
    const stickyVariantTitle = stickyAtc?.querySelector('[data-sticky-variant-title]');
    const stickyOptionSelects = Array.from(stickyAtc?.querySelectorAll('[data-sticky-option-select]') || []);
    const stickyCustomSelects = Array.from(stickyAtc?.querySelectorAll('[data-sticky-custom-select]') || []);
    const stickyCustomOptions = Array.from(stickyAtc?.querySelectorAll('[data-sticky-custom-option]') || []);
    const inventory = root.querySelector('[data-inventory-status]');
    const optionInputs = Array.from(root.querySelectorAll('[data-option-input]'));
    const fallbackColorOptionIndex = product.options.findIndex((name) => {
      const optionName = String(name || '').toLowerCase();
      return optionName.includes('color') || optionName.includes('colour');
    });
    const fallbackSizeOptionIndex = product.options.findIndex((name) => {
      const optionName = String(name || '').toLowerCase();
      return optionName.includes('size');
    });
    const colorOptionIndex = configuredColorOptionIndex > -1 ? configuredColorOptionIndex : fallbackColorOptionIndex;
    const sizeOptionIndex = configuredSizeOptionIndex > -1 ? configuredSizeOptionIndex : fallbackSizeOptionIndex;
    const requiresSizeSelection = !isGiftCardProduct && sizeOptionIndex > -1 && optionInputs.some((input) => Number(input.dataset.optionIndex) === sizeOptionIndex);
    const selectSizeText = 'Select size';
    const notifyMeText = 'Notify me';
    let isMainAddButtonVisible = true;
    let currentVariant = null;
    let notifyButtonResetTimer = null;
    const backInStockModal = root.querySelector('[data-back-in-stock-modal]');

    if (backInStockModal && backInStockModal.parentElement !== document.body) {
      document.body.appendChild(backInStockModal);
    }

    const backInStockCloseButtons = backInStockModal ? Array.from(backInStockModal.querySelectorAll('[data-back-in-stock-close]')) : [];
    const backInStockForm = backInStockModal?.querySelector('[data-back-in-stock-form]');
    const backInStockEmail = backInStockModal?.querySelector('[data-back-in-stock-email]');
    const backInStockPhone = backInStockModal?.querySelector('[data-back-in-stock-phone]');
    const backInStockFeedback = backInStockModal?.querySelector('[data-back-in-stock-feedback]');
    const backInStockSubmit = backInStockModal?.querySelector('[data-back-in-stock-submit]');
    const backInStockSuccess = backInStockModal?.querySelector('[data-back-in-stock-success]');
    const backInStockVariantTitle = backInStockModal?.querySelector('[data-back-in-stock-variant-title]');
    let backInStockVariant = null;
    let backInStockLastFocusedElement = null;
    let backInStockCloseTimer = null;

    const getBackInStockFocusableElements = () => {
      if (!backInStockModal) return [];

      return Array.from(backInStockModal.querySelectorAll(sizeGuideFocusableSelector)).filter((element) => {
        return element instanceof HTMLElement && !element.hidden && element.offsetParent !== null;
      });
    };

    const isBackInStockModalOpen = () => Boolean(backInStockModal && !backInStockModal.hidden);

    const getVariantDisplayTitle = (variant) => {
      const optionValues = Array.isArray(variant?.options)
        ? variant.options.filter((value) => value && value !== 'Default Title')
        : [];

      return optionValues.join(' / ') || variant?.title || product.title || 'Selected item';
    };

    const setBackInStockFeedback = (message = '', isError = true) => {
      if (!backInStockFeedback) return;

      backInStockFeedback.hidden = !message;
      backInStockFeedback.textContent = message;
      backInStockFeedback.classList.toggle('product-back-in-stock-modal__feedback--error', isError);
    };

    const setBackInStockLoading = (isLoading) => {
      if (backInStockSubmit) {
        backInStockSubmit.disabled = isLoading;
        backInStockSubmit.classList.toggle('is-loading', isLoading);
        backInStockSubmit.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        backInStockSubmit.textContent = isLoading ? 'Saving...' : notifyMeText;
      }
    };

    const getBackInStockDebugEnabled = () => {
      const params = new URLSearchParams(window.location.search);
      return params.get('bis_debug') === '1' || localStorage.getItem('wobBackInStockDebug') === 'true';
    };

    const getRedoBackInStockConfig = () => {
      const redoStorefront = window.redoStorefront || {};
      const redoEnv = redoStorefront.env || {};
      const dataset = backInStockModal?.dataset || {};
      const serverUrl = String(redoEnv.REDO_SHOPIFY_SERVER_URL || dataset.redoServerUrl || 'https://shopify-server.getredo.com').replace(/\/+$/, '');
      const widgetId = String(window.redoWidgetId || dataset.redoWidgetId || '').trim();
      const storeUrl = String(redoStorefront.storeUrl || window.Shopify?.shop || window.Shopify?.Checkout?.apiHost || dataset.redoStoreUrl || '').trim();

      return { serverUrl, widgetId, storeUrl };
    };

    const postRedoBackInStockRpc = async (method, input) => {
      const { serverUrl, widgetId, storeUrl } = getRedoBackInStockConfig();

      if (!widgetId || !storeUrl) {
        throw new Error('Redo back-in-stock is not configured.');
      }

      const response = await fetch(`${serverUrl}/rpc/${method}`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'X-WIDGET-ID': widgetId,
          'X-SHOPIFY-STORE-URL': storeUrl
        },
        body: JSON.stringify({ input })
      });

      if (!response.ok) {
        throw new Error(`Redo back-in-stock request failed with ${response.status}.`);
      }

      const data = await response.json();

      if (!data || typeof data !== 'object' || !('output' in data)) {
        throw new Error('Redo back-in-stock response was not usable.');
      }

      return data.output;
    };

    const subscribeBackInStockWithRedo = async ({ email, phone, variant }) => {
      const productId = String(product.id || '');
      const variantId = String(variant?.id || '');

      if (!productId || !variantId) {
        throw new Error('Back-in-stock product or variant is missing.');
      }

      const input = {
        productId,
        variantId
      };

      if (email) input.email = email;
      if (phone) input.phone = phone;

      const output = await postRedoBackInStockRpc('submitBackInStockForm', input);

      if (!output?.success) {
        throw new Error('Redo back-in-stock request was not successful.');
      }

      const detail = {
        provider: 'redo',
        productId,
        variantId,
        contactMethods: [
          email ? 'email' : '',
          phone ? 'sms' : ''
        ].filter(Boolean),
        productTitle: product.title || document.title,
        variantTitle: getVariantDisplayTitle(variant),
        submittedAt: new Date().toISOString()
      };

      window.WOBBackInStockLastSubmission = detail;
      document.dispatchEvent(new CustomEvent('wob:back-in-stock:submission', {
        bubbles: true,
        detail
      }));

      if (getBackInStockDebugEnabled()) {
        console.info('[WOB back in stock] Submitted to Redo', detail);
      }

      return true;
    };

    const closeBackInStockModal = ({ restoreFocus = true } = {}) => {
      if (!backInStockModal || backInStockModal.hidden) return;

      window.clearTimeout(backInStockCloseTimer);
      backInStockModal.classList.remove('product-back-in-stock-modal--open');
      backInStockModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('product-back-in-stock-modal-is-open');
      document.body.classList.remove('product-back-in-stock-modal-is-open');

      backInStockCloseTimer = window.setTimeout(() => {
        backInStockModal.hidden = true;
      }, 180);

      if (restoreFocus && backInStockLastFocusedElement instanceof HTMLElement) {
        backInStockLastFocusedElement.focus({ preventScroll: true });
      }
    };

    const openThemeBackInStockModal = (variant) => {
      if (!backInStockModal || !variant) return false;

      window.clearTimeout(backInStockCloseTimer);
      backInStockVariant = variant;
      backInStockLastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      if (backInStockVariantTitle) backInStockVariantTitle.textContent = getVariantDisplayTitle(variant);
      if (backInStockEmail) backInStockEmail.value = '';
      if (backInStockPhone) backInStockPhone.value = '';
      if (backInStockForm) backInStockForm.hidden = false;
      if (backInStockSuccess) backInStockSuccess.hidden = true;
      setBackInStockFeedback();
      setBackInStockLoading(false);

      backInStockModal.hidden = false;
      backInStockModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('product-back-in-stock-modal-is-open');
      document.body.classList.add('product-back-in-stock-modal-is-open');

      window.requestAnimationFrame(() => {
        backInStockModal.classList.add('product-back-in-stock-modal--open');
        const focusTarget = backInStockEmail || backInStockModal.querySelector('[data-back-in-stock-close]') || backInStockModal;
        focusTarget.focus({ preventScroll: true });
      });

      return true;
    };

    const trapBackInStockFocus = (event) => {
      if (event.key !== 'Tab' || !isBackInStockModalOpen()) return;

      const focusableElements = getBackInStockFocusableElements();

      if (!focusableElements.length) {
        event.preventDefault();
        backInStockModal.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const handleBackInStockSubmit = async (event) => {
      event.preventDefault();

      if (!backInStockVariant || (!backInStockEmail && !backInStockPhone)) return;

      const email = backInStockEmail?.value.trim() || '';
      const phone = backInStockPhone?.value.trim() || '';

      if (!email && !phone) {
        setBackInStockFeedback('Enter an email address or SMS number.', true);
        (backInStockEmail || backInStockPhone)?.focus({ preventScroll: true });
        return;
      }

      if (email && !backInStockEmail.checkValidity()) {
        backInStockEmail.reportValidity();
        return;
      }

      setBackInStockLoading(true);
      setBackInStockFeedback();

      try {
        const submitted = await subscribeBackInStockWithRedo({ email, phone, variant: backInStockVariant });
        if (!submitted) throw new Error('Back-in-stock request was not submitted.');

        if (backInStockForm) backInStockForm.hidden = true;
        if (backInStockSuccess) backInStockSuccess.hidden = false;
        backInStockSuccess?.querySelector('[data-back-in-stock-close]')?.focus({ preventScroll: true });
      } catch (error) {
        setBackInStockFeedback('We could not save that request right now. Please try again.', true);
      } finally {
        setBackInStockLoading(false);
      }
    };

    if (backInStockModal) {
      backInStockCloseButtons.forEach((button) => {
        button.addEventListener('click', () => closeBackInStockModal());
      });

      backInStockForm?.addEventListener('submit', handleBackInStockSubmit);

      document.addEventListener('keydown', (event) => {
        if (!isBackInStockModalOpen()) return;

        if (event.key === 'Tab' || event.key === 'Escape') {
          event.stopPropagation();
        }

        trapBackInStockFocus(event);

        if (event.key === 'Escape') {
          event.preventDefault();
          closeBackInStockModal();
        }
      }, true);
    }

    const syncSelectedOptionLabels = () => {
      root.querySelectorAll('[data-selected-option-value]').forEach((label) => {
        const option = label.closest('[data-option-index]');
        const optionIndex = option?.dataset.optionIndex;
        if (optionIndex === undefined) return;

        const checkedInput = root.querySelector(`[data-option-input][data-option-index="${optionIndex}"]:checked`);
        const rawValue = checkedInput?.value || '';
        label.dataset.swatchLabelRaw = rawValue;
        label.textContent = window.WobSwatches?.getSwatchLabel?.(rawValue) || rawValue;
      });
    };

    const setStickyAtcVisibility = () => {
      if (!stickyAtc) return;

      const rootTop = root.getBoundingClientRect().top + window.scrollY;
      const addButtonRect = addButton?.getBoundingClientRect();
      const hasScrolledIntoProduct = window.scrollY > rootTop + 320;

      if (addButtonRect) {
        isMainAddButtonVisible = addButtonRect.bottom > 0 && addButtonRect.top < window.innerHeight;
      }

      const shouldShow = hasScrolledIntoProduct && !isMainAddButtonVisible;

      stickyAtc.classList.toggle('is-visible', shouldShow);
      stickyAtc.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      document.documentElement.style.setProperty(
        '--product-sticky-atc-toast-offset',
        shouldShow ? `${Math.ceil(stickyAtc.getBoundingClientRect().height)}px` : '0px'
      );
    };

    if (stickyAtc && addButton) {
      if ('IntersectionObserver' in window) {
        const stickyObserver = new IntersectionObserver((entries) => {
          isMainAddButtonVisible = entries.some((entry) => entry.isIntersecting);
          setStickyAtcVisibility();
        }, { threshold: 0.1 });

        stickyObserver.observe(addButton);
      }

      window.addEventListener('scroll', setStickyAtcVisibility, { passive: true });
      window.addEventListener('resize', setStickyAtcVisibility);
      setStickyAtcVisibility();
    }

    const getSelectedOptions = () => {
      return product.options.map((optionName, optionIndex) => {
        const checkedInput = root.querySelector(`[data-option-input][data-option-index="${optionIndex}"]:checked`);
        return checkedInput?.value || '';
      });
    };

    const isOptionValueVisible = (optionIndex, optionValue, selectedOptions = getSelectedOptions()) => {
      return visibleVariants.some((variant) => {
        if (variant.options?.[optionIndex] !== optionValue) return false;

        return selectedOptions.every((selectedValue, selectedIndex) => {
          return selectedIndex >= optionIndex || !selectedValue || variant.options?.[selectedIndex] === selectedValue;
        });
      });
    };

    const isOptionValueAvailable = (optionIndex, optionValue, selectedOptions = getSelectedOptions()) => {
      return visibleVariants.some((variant) => {
        if (!variant.available || variant.options?.[optionIndex] !== optionValue) return false;

        return selectedOptions.every((selectedValue, selectedIndex) => {
          return selectedIndex >= optionIndex || !selectedValue || variant.options?.[selectedIndex] === selectedValue;
        });
      });
    };

    const syncOptionValueVisibility = (selectedOptions = getSelectedOptions()) => {
      let selectionChanged = false;

      optionInputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);

        if (!Number.isInteger(optionIndex)) return;

        const isVisible = isOptionValueVisible(optionIndex, input.value, selectedOptions);
        const isAvailable = isOptionValueAvailable(optionIndex, input.value, selectedOptions);
        const label = input.closest('[data-option-value-label]') || input.closest('label');

        if (label) {
          label.hidden = !isVisible;
          label.classList.toggle('product-option-tag--sold-out', isVisible && !isAvailable && label.classList.contains('product-option-tag'));
          label.classList.toggle('product-option-swatch--sold-out', isVisible && !isAvailable && label.classList.contains('product-option-swatch'));
          label.setAttribute('aria-disabled', isVisible && !isAvailable ? 'true' : 'false');
        }

        if (isVisible && !isAvailable) {
          input.dataset.optionSoldOut = 'true';
        } else {
          delete input.dataset.optionSoldOut;
        }

        input.disabled = !isVisible;

        if (!isVisible && input.checked) {
          input.checked = false;
          selectionChanged = true;
        }
      });

      stickyOptionSelects.forEach((stickySelect) => {
        const optionIndex = Number(stickySelect.dataset.optionIndex);

        if (!Number.isInteger(optionIndex)) return;

        Array.from(stickySelect.options).forEach((option) => {
          if (!option.value) return;

          const isVisible = isOptionValueVisible(optionIndex, option.value, selectedOptions);

          const isAvailable = isOptionValueAvailable(optionIndex, option.value, selectedOptions);

          option.hidden = !isVisible;
          option.disabled = !isVisible;
          option.dataset.optionSoldOut = isVisible && !isAvailable ? 'true' : 'false';
        });
      });

      stickyCustomOptions.forEach((option) => {
        const optionIndex = Number(option.dataset.optionIndex);

        if (!Number.isInteger(optionIndex)) return;

        const isVisible = isOptionValueVisible(optionIndex, option.value, selectedOptions);
        const isAvailable = isOptionValueAvailable(optionIndex, option.value, selectedOptions);

        option.hidden = !isVisible;
        option.disabled = !isVisible;
        option.classList.toggle('is-sold-out', isVisible && !isAvailable);
        option.setAttribute('aria-disabled', isVisible && !isAvailable ? 'true' : 'false');
      });

      return selectionChanged;
    };

    const findVariantFromOptionValues = (selectedOptions) => {
      return visibleVariants.find((variant) => {
        return variant.options.every((value, index) => value === selectedOptions[index]);
      });
    };

    const isMissingRequiredSize = (selectedOptions = getSelectedOptions()) => {
      return requiresSizeSelection && !selectedOptions[sizeOptionIndex];
    };

    const findVariantFromPartialOptions = (selectedOptions) => {
      const matches = visibleVariants.filter((variant) => {
        return selectedOptions.every((value, index) => !value || variant.options[index] === value);
      });

      return matches.find((variant) => variant.available) || matches[0] || null;
    };

    const findVariantFromOptions = () => {
      return findVariantFromOptionValues(getSelectedOptions());
    };

    const getStickySelectedOptions = () => {
      const currentOptions = getSelectedOptions();

      return product.options.map((optionName, optionIndex) => {
        const stickySelect = stickyOptionSelects.find((item) => Number(item.dataset.optionIndex) === optionIndex);
        return stickySelect?.value || currentOptions[optionIndex] || '';
      });
    };

    const syncOptionInputsFromOptions = (selectedOptions) => {
      optionInputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);

        if (!Number.isInteger(optionIndex)) return;

        input.checked = selectedOptions[optionIndex] === input.value;
      });
    };

    const syncOptionInputsForVariant = (variant) => {
      syncOptionInputsFromOptions(variant.options);
    };

    const syncStickyOptionSelectsFromOptions = (selectedOptions) => {
      stickyOptionSelects.forEach((stickySelect) => {
        const optionIndex = Number(stickySelect.dataset.optionIndex);
        const nextValue = Number.isInteger(optionIndex) ? selectedOptions[optionIndex] || '' : '';

        if (stickySelect.value !== nextValue) {
          stickySelect.value = nextValue;
        }
      });
    };

    const syncStickyOptionSelects = (variant) => {
      syncStickyOptionSelectsFromOptions(variant.options);
    };

    const syncStickyCustomSelectsFromOptions = (selectedOptions) => {
      stickyCustomSelects.forEach((customSelect) => {
        const optionIndex = Number(customSelect.dataset.optionIndex);
        const selectedValue = Number.isInteger(optionIndex) ? selectedOptions[optionIndex] : '';
        const valueLabel = customSelect.querySelector('[data-sticky-custom-select-value]');

        if (valueLabel) {
          valueLabel.textContent = selectedValue || (optionIndex === sizeOptionIndex ? selectSizeText : '');
        }

        customSelect.querySelectorAll('[data-sticky-custom-option]').forEach((option) => {
          option.setAttribute('aria-selected', option.value === selectedValue ? 'true' : 'false');
        });
      });
    };

    const syncStickyCustomSelects = (variant) => {
      syncStickyCustomSelectsFromOptions(variant.options);
    };

    const updatePriceForVariant = (variant) => {
      if (window.WobPromotionPricing?.updateVariant) {
        priceContainers.forEach((price) => {
          window.WobPromotionPricing.updateVariant(price, {
            productId: product.id,
            variantId: variant.id,
            priceCents: variant.price,
            compareAtPriceCents: variant.compare_at_price || 0
          });
        });
        return;
      }
      const onSale = Number(variant?.compare_at_price || 0) > Number(variant?.price || 0);

      priceContainers.forEach((price) => {
        const currentPrice = price.querySelector('[data-price-current]');
        const currentLabel = price.querySelector('[data-price-current-label]');
        let compareLabel = price.querySelector('[data-price-compare-label]');
        let comparePrice = price.querySelector('[data-price-compare]');
        let discountLabel = price.querySelector('[data-price-discount]');

        if (currentPrice) currentPrice.textContent = formatMoney(variant.price);
        if (currentLabel) currentLabel.textContent = onSale ? 'Sale price' : 'Regular price';
        price.classList.toggle('price--on-sale', onSale);

        if (onSale) {
          if (!compareLabel) {
            compareLabel = document.createElement('span');
            compareLabel.className = 'visually-hidden';
            compareLabel.dataset.priceCompareLabel = '';
            compareLabel.textContent = 'Regular price';
            price.append(compareLabel);
          }

          if (!comparePrice) {
            comparePrice = document.createElement('s');
            comparePrice.className = 'price__compare';
            comparePrice.dataset.priceCompare = '';
            price.append(comparePrice);
          }

          comparePrice.textContent = formatMoney(variant.compare_at_price);

          const salePercent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          if (!discountLabel) {
            discountLabel = document.createElement('span');
            discountLabel.className = 'price__discount';
            discountLabel.dataset.priceDiscount = '';
            price.append(discountLabel);
          }
          discountLabel.textContent = `${salePercent}% off`;
          discountLabel.setAttribute('aria-label', `${salePercent} percent off`);
          price.append(discountLabel);
        } else {
          compareLabel?.remove();
          comparePrice?.remove();
          discountLabel?.remove();
        }
      });
    };

    const setSelectSizeState = (selectedOptions = getSelectedOptions()) => {
      currentVariant = null;
      const selectionChanged = syncOptionValueVisibility(selectedOptions);
      const nextSelectedOptions = selectionChanged ? getSelectedOptions() : selectedOptions;
      const previewVariant = findVariantFromPartialOptions(nextSelectedOptions);

      if (previewVariant) {
        select.value = String(previewVariant.id);
        updatePriceForVariant(previewVariant);
        syncMediaForVariant(previewVariant);
      }

      if (addButton) addButton.disabled = true;
      if (addText) addText.textContent = selectSizeText;
      if (stickyAddButton) stickyAddButton.disabled = true;
      if (stickyAddText) stickyAddText.textContent = selectSizeText;
      if (stickyVariantTitle) stickyVariantTitle.textContent = nextSelectedOptions.filter(Boolean).join(' / ') || selectSizeText;
      syncBackInStockButtons(null);

      if (inventory) {
        inventory.hidden = true;
        inventory.textContent = '';
      }

      syncSelectedOptionLabels();
      syncStickyOptionSelectsFromOptions(nextSelectedOptions);
      syncStickyCustomSelectsFromOptions(nextSelectedOptions);
    };

    const setUnavailableState = () => {
      currentVariant = null;
      if (addButton) addButton.disabled = true;
      if (addText) addText.textContent = 'Unavailable';
      if (stickyAddButton) stickyAddButton.disabled = true;
      if (stickyAddText) stickyAddText.textContent = 'Unavailable';
      if (inventory) {
        inventory.hidden = false;
        inventory.textContent = 'Unavailable';
      }
      if (mediaNote) mediaNote.hidden = true;
      syncBackInStockButtons(null);
    };

    const syncBackInStockUrl = (variant) => {
      if (!variant?.id) return;

      const url = new URL(window.location.href);

      if (url.searchParams.get('variant') === String(variant.id)) return;

      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    const syncBackInStockButtons = (variant, { loading = false } = {}) => {
      const isBackInStock = Boolean(variant && !variant.available);

      [
        { button: addButton, text: addText },
        { button: stickyAddButton, text: stickyAddText }
      ].forEach(({ button, text }) => {
        if (!button) return;

        button.type = isBackInStock ? 'button' : 'submit';

        if (isBackInStock) {
          button.disabled = false;
          button.dataset.backInStockActive = 'true';
          button.dataset.variantId = String(variant.id);
          if (text) text.textContent = loading ? 'Opening...' : notifyMeText;
          return;
        }

        delete button.dataset.backInStockActive;
        delete button.dataset.variantId;
      });
    };

    const openBackInStockPopup = () => {
      const variant = currentVariant || findVariantFromOptions();

      if (!variant || variant.available) return;

      syncBackInStockUrl(variant);
      syncBackInStockButtons(variant, { loading: true });
      window.clearTimeout(notifyButtonResetTimer);
      notifyButtonResetTimer = window.setTimeout(() => {
        syncBackInStockButtons(variant);
      }, 1400);

      openThemeBackInStockModal(variant);
    };

    const dispatchVariantPending = (reason, selectedOptions = getSelectedOptions()) => {
      document.dispatchEvent(new CustomEvent('variant:pending', {
        bubbles: true,
        detail: { reason, selectedOptions, product }
      }));
    };

    const updateMediaNote = (variant, isUsingFallbackMedia = false) => {
      if (!mediaNote) return;

      const colorValue = colorOptionIndex > -1 ? variant?.options?.[colorOptionIndex] || '' : '';

      if (isUsingFallbackMedia && colorValue) {
        if (mediaNoteText) mediaNoteText.textContent = "Oops, we couldn't find the images for this color";
        mediaNote.hidden = false;
        return;
      }

      mediaNote.hidden = true;
    };

    const syncMediaForVariant = (variant) => {
      mediaActivationRequest += 1;

      const colorHandle = colorOptionIndex > -1 ? toHandle(variant.options[colorOptionIndex]) : '';
      let allowedPanels = mediaPanels;
      let isUsingFallbackMedia = false;

      if (hasMappedColorMedia && colorHandle) {
        allowedPanels = mediaPanels.filter((panel) => getMediaColors(panel).includes(colorHandle));

        if (!allowedPanels.length) {
          allowedPanels = mediaPanels;
          isUsingFallbackMedia = true;
        }
      }

      currentAllowedPanels = allowedPanels;
      updateMediaNote(variant, isUsingFallbackMedia);

      if (mediaCarouselInteracted) {
        loadCurrentVariantFullSizeImages();
      }

      const allowedMediaIds = new Set(allowedPanels.map((panel) => panel.dataset.mediaId));

      mediaTriggers.forEach((trigger) => {
        const isAllowed = allowedMediaIds.has(trigger.dataset.mediaTrigger);
        trigger.hidden = !isAllowed;
        trigger.setAttribute('aria-current', 'false');
        if (isAllowed) loadDeferredImages(trigger);
      });

      const preferredMediaId = String(variant.featured_media?.id || variant.featured_image?.id || '');
      const nextMediaId = allowedMediaIds.has(preferredMediaId) ? preferredMediaId : allowedPanels[0]?.dataset.mediaId;

      if (nextMediaId) {
        activateMediaWhenReady(nextMediaId, allowedPanels, mediaInitialized ? '' : 'none');
      } else {
        mediaPanels.forEach((panel) => {
          panel.hidden = true;
          panel.classList.remove('product-media--active');
        });
        updateCarouselButtons();
      }

      mediaInitialized = true;
      window.requestAnimationFrame(updateThumbnailScrollbar);
    };

    const updateVariant = ({ updateUrl = true, dispatchChange = true } = {}) => {
      const selectedOptions = getSelectedOptions();

      if (isMissingRequiredSize(selectedOptions)) {
        setSelectSizeState(selectedOptions);

        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.delete('variant');
          window.history.replaceState({}, '', url.toString());
        }

        if (dispatchChange) {
          dispatchVariantPending('missing-required-option', selectedOptions);
        }

        return;
      }

      const variant = visibleVariants.find((item) => String(item.id) === select.value);
      if (!variant) {
        setUnavailableState();

        if (dispatchChange) {
          dispatchVariantPending('unavailable-options', selectedOptions);
        }

        return;
      }

      currentVariant = variant;
      updatePriceForVariant(variant);

      if (addButton) addButton.disabled = false;
      if (addText) addText.textContent = variant.available ? 'Add to bag' : notifyMeText;
      if (stickyAddButton) stickyAddButton.disabled = false;
      if (stickyAddText) stickyAddText.textContent = variant.available ? 'Add to bag' : notifyMeText;
      if (stickyVariantTitle) stickyVariantTitle.textContent = variant.title;
      syncBackInStockButtons(variant);

      if (inventory) {
        inventory.hidden = true;
        inventory.textContent = '';
      }

      syncOptionInputsForVariant(variant);
      syncOptionValueVisibility(variant.options);
      syncSelectedOptionLabels();
      syncStickyOptionSelects(variant);
      syncStickyCustomSelects(variant);

      syncMediaForVariant(variant);

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (dispatchChange) {
        document.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant, product }
        }));
      }
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        let selectedOptions = getSelectedOptions();

        if (syncOptionValueVisibility(selectedOptions)) {
          selectedOptions = getSelectedOptions();
        }

        if (isMissingRequiredSize(selectedOptions)) {
          updateVariant();
          return;
        }

        const variant = findVariantFromOptionValues(selectedOptions);

        if (!variant) {
          setUnavailableState();
          dispatchVariantPending('unavailable-options', selectedOptions);
          return;
        }

        select.value = String(variant.id);
        updateVariant();
      });
    });

    stickyOptionSelects.forEach((stickySelect) => {
      stickySelect.addEventListener('change', () => {
        let selectedOptions = getStickySelectedOptions();

        syncOptionInputsFromOptions(selectedOptions);

        if (syncOptionValueVisibility(selectedOptions)) {
          selectedOptions = getSelectedOptions();
          syncStickyOptionSelectsFromOptions(selectedOptions);
        }

        if (isMissingRequiredSize(selectedOptions)) {
          setSelectSizeState(selectedOptions);
          dispatchVariantPending('missing-required-option', selectedOptions);
          return;
        }

        const variant = findVariantFromOptionValues(selectedOptions);

        syncSelectedOptionLabels();

        if (!variant) {
          setUnavailableState();
          dispatchVariantPending('unavailable-options', selectedOptions);
          if (stickyVariantTitle) stickyVariantTitle.textContent = selectedOptions.filter(Boolean).join(' / ');
          syncStickyCustomSelectsFromOptions(selectedOptions);
          return;
        }

        select.value = String(variant.id);
        updateVariant();
      });
    });

    stickyCustomOptions.forEach((option) => {
      option.addEventListener('click', () => {
        const optionIndex = Number(option.dataset.optionIndex);
        const stickySelect = stickyOptionSelects.find((item) => Number(item.dataset.optionIndex) === optionIndex);
        const customSelect = option.closest('[data-sticky-custom-select]');

        if (!stickySelect) return;

        stickySelect.value = option.value;
        stickySelect.dispatchEvent(new Event('change', { bubbles: true }));

        if (customSelect instanceof HTMLDetailsElement) {
          customSelect.open = false;
          customSelect.querySelector('summary')?.focus({ preventScroll: true });
        }
      });
    });

    [addButton, stickyAddButton].forEach((button) => {
      button?.addEventListener('click', (event) => {
        if (button.dataset.backInStockActive !== 'true') return;

        event.preventDefault();
        openBackInStockPopup();
      });
    });

    form.addEventListener('submit', (event) => {
      const selectedOptions = getSelectedOptions();
      const selectedVariant = findVariantFromOptionValues(selectedOptions);

      if (selectedVariant && !selectedVariant.available) {
        currentVariant = selectedVariant;
        event.preventDefault();
        openBackInStockPopup();
        return;
      }

      if (!isMissingRequiredSize(selectedOptions) && selectedVariant) {
        select.value = String(selectedVariant.id);
        return;
      }

      event.preventDefault();
      setSelectSizeState(selectedOptions);

      const firstSizeInput = optionInputs.find((input) => Number(input.dataset.optionIndex) === sizeOptionIndex && !input.disabled);

      firstSizeInput?.focus({ preventScroll: true });
      firstSizeInput?.closest('.product-option')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });

    select.addEventListener('change', updateVariant);
    updateVariant({ updateUrl: false, dispatchChange: false });
  });
})();
